import SWRWrapper from "@/components/Context/SWRWrapper";
import ErrorBoundary from "@/components/ErrorBoundary";
import PortalComponentWrapper from "@/components/PortalComponentWrapper";
import Layout from "@/components/Ui/Layout";
import UserContext from "@/components/UserContext";
import { POSTHOG_HOST, POSTHOG_KEY } from "@/constants/posthog";
import { AuthTokenProvider } from "@/contexts/auth-token-context";
import { ModalContentProvider } from "@/contexts/modal-context";
import { SelectedDatesProvider } from "@/contexts/selected-dates-context";
import { SesamyAuthTokenProvider } from "@/contexts/sesamy-auth-token-context";
import { VendorContentProvider } from "@/contexts/vendor-context";
import "@/styles/globals.css";
import { Auth0Provider } from "@auth0/auth0-react";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import type { AppProps } from "next/app";
import Head from "next/head";
import { Router } from "next/router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";

TimeAgo.setDefaultLocale(en.locale);
TimeAgo.addLocale(en);

if (!process.env.NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL) {
  throw new Error("Define NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL");
}
if (!process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID) {
  throw new Error("Define NEXT_PUBLIC_AUTH0_CLIENT_ID");
}

const MyApp = ({ Component, pageProps }: AppProps) => {
  useEffect(() => {
    if (!POSTHOG_KEY) return;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
    });

    const handleRouteChange = () => posthog?.capture("$pageview");

    Router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <Auth0Provider
        domain={process.env.NEXT_PUBLIC_SESAMY_TOKEN_FULL_URL!}
        clientId="sesamy"
        authorizationParams={{
          redirect_uri:
            typeof window !== "undefined" ? window.location.origin : "",
        }}
      >
        <AuthTokenProvider>
          <VendorContentProvider>
            <SesamyAuthTokenProvider>
              <SelectedDatesProvider>
                <PortalComponentWrapper>
                  <SWRWrapper>
                    <ModalContentProvider>
                      <Head>
                        <title>Sesamy Portal</title>
                      </Head>
                      <ErrorBoundary>
                        <UserContext />
                        <Layout>
                          <Component {...pageProps} />
                        </Layout>
                      </ErrorBoundary>
                      <div id="drawer-root" />
                      <div id="modal-root" />
                      <div
                        data-apply-playwright-mask
                        className="pointer-events-none fixed right-0 bottom-0 left-0 z-20 h-20"
                      />
                    </ModalContentProvider>
                  </SWRWrapper>
                </PortalComponentWrapper>
              </SelectedDatesProvider>
            </SesamyAuthTokenProvider>
          </VendorContentProvider>
        </AuthTokenProvider>
      </Auth0Provider>
    </PostHogProvider>
  );
};

export default MyApp;
